// Agent Lifecycle Controls — Quarantine / Pause / Resume for a given agent
// identity. Engine routes:
//   POST /api/v1/agents/{agent_identity_id}/quarantine|pause|resume
//
// Authorization: only Director or an owning human of the agent's active
// delegations may change lifecycle. Non-Directors see disabled buttons with
// a tooltip explaining the limitation. We can't autodetect "owning human"
// from the client because GET /api/v1/bond-delegations?owner_id= has not
// shipped — see Widget 4.
//
// Client-side validation: quarantine_until must be in the future.
//
// On success, mutate the active-runs SWR key so Widget 6 reflects the
// halt count immediately.

import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { pauseAgent, quarantineAgent, resumeAgent } from '@/api/agents';
import type { AgentLifecycleResult } from '@/api/agentBoardTypes';
import { EngineHttpError } from '@/lib/engineHttp';
import { useEscapeKey } from '@/lib/useEscapeKey';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

type Operation = 'quarantine' | 'pause' | 'resume';

interface ModalState {
  operation: Operation;
}

function statusColor(status: string | undefined): string {
  switch (status) {
    case 'active':
      return 'var(--sp-ok)';
    case 'paused':
      return 'var(--sp-warn)';
    case 'quarantined':
      return 'var(--sp-error)';
    default:
      return 'var(--sp-text-dim)';
  }
}

function relativeUntil(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function AgentLifecycleControlsWidget({ context }: WidgetProps) {
  const { mutate: globalMutate } = useSWRConfig();
  const [agentIdentityId, setAgentIdentityId] = useState<string>('');
  const [submittedAgentId, setSubmittedAgentId] = useState<string | null>(null);
  const [latest, setLatest] = useState<AgentLifecycleResult | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [reason, setReason] = useState('');
  const [quarantineUntil, setQuarantineUntil] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canAct = context.isDirector;
  const disabledReason =
    'Only the Director or an owning human of the agent\'s active delegations can change lifecycle state.';

  function flashToast(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 5000);
  }

  function openModal(op: Operation) {
    setModal({ operation: op });
    setReason('');
    setQuarantineUntil('');
    setValidationError(null);
    setActionError(null);
  }

  function closeModal() {
    setModal(null);
    setReason('');
    setQuarantineUntil('');
    setValidationError(null);
    setSubmitting(false);
  }

  async function submit() {
    if (!modal || !submittedAgentId) return;
    const body: { reason?: string; quarantine_until?: string } = {};
    if (reason.trim()) body.reason = reason.trim();

    if (modal.operation === 'quarantine' && quarantineUntil) {
      const ts = new Date(quarantineUntil).getTime();
      if (Number.isNaN(ts)) {
        setValidationError('Invalid date.');
        return;
      }
      if (ts <= Date.now()) {
        setValidationError('quarantine_until must be in the future.');
        return;
      }
      body.quarantine_until = new Date(ts).toISOString();
    }

    setSubmitting(true);
    setActionError(null);
    try {
      let result: AgentLifecycleResult;
      switch (modal.operation) {
        case 'quarantine':
          result = await quarantineAgent(submittedAgentId, body);
          break;
        case 'pause':
          result = await pauseAgent(submittedAgentId, body);
          break;
        case 'resume':
          result = await resumeAgent(submittedAgentId, body);
          break;
      }
      setLatest(result);
      flashToast(
        `${result.operation}: halted ${result.halted_runs} run(s), cancelled ${result.cancelled_approvals} approval(s) across ${result.affected_delegations} delegation(s).`,
      );
      void globalMutate('agent-board:runs:active');
      closeModal();
    } catch (err) {
      if (err instanceof EngineHttpError) {
        setActionError(`${err.status}: ${err.message}`);
      } else {
        setActionError(err instanceof Error ? err.message : 'Unknown error');
      }
      setSubmitting(false);
    }
  }

  function handleSubmitId(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = agentIdentityId.trim();
    if (trimmed) {
      setSubmittedAgentId(trimmed);
      setLatest(null);
      setActionError(null);
    }
  }

  return (
    <div id="widget-agent-lifecycle-controls" className="sp-widget">
      <div className="sp-widget-header">
        <span>Agent Lifecycle Controls</span>
        {submittedAgentId ? (
          <button
            type="button"
            onClick={() => {
              setSubmittedAgentId(null);
              setAgentIdentityId('');
              setLatest(null);
            }}
            style={{
              fontSize: 11,
              fontWeight: 400,
              letterSpacing: 0,
              border: 'none',
              background: 'transparent',
              color: 'var(--sp-text-dim)',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="sp-widget-body" style={{ padding: 12, fontSize: 12 }}>
        <form onSubmit={handleSubmitId} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input
            type="text"
            value={agentIdentityId}
            onChange={(e) => setAgentIdentityId(e.target.value)}
            placeholder="Agent identity (UUID or 'claude-ide')"
            aria-label="Agent identity"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: '1px solid var(--sp-border)',
              borderRadius: 3,
              padding: '4px 6px',
              color: 'var(--sp-text)',
              fontFamily: 'inherit',
              fontSize: 12,
            }}
          />
          <button
            type="submit"
            disabled={!agentIdentityId.trim()}
            style={{
              fontSize: 11,
              padding: '4px 10px',
              border: '1px solid var(--sp-border)',
              background: 'transparent',
              color: 'var(--sp-accent)',
              borderRadius: 3,
              cursor: agentIdentityId.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Load
          </button>
        </form>

        {!submittedAgentId ? (
          <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
            Enter an agent identity to manage its lifecycle.
          </div>
        ) : (
          <div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <span className="mono" style={{ fontSize: 11 }}>
                {submittedAgentId}
              </span>
              <span
                style={{
                  fontSize: 9,
                  padding: '1px 5px',
                  borderRadius: 2,
                  background: statusColor(latest?.status),
                  color: 'var(--sp-bg, #000)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                {latest?.status ?? 'unknown'}
              </span>
              {latest?.quarantine_until ? (
                <span style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
                  quarantined for {relativeUntil(latest.quarantine_until)}
                </span>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <LifecycleButton
                label="Quarantine"
                color="var(--sp-error)"
                onClick={() => openModal('quarantine')}
                disabled={!canAct}
                title={canAct ? undefined : disabledReason}
              />
              <LifecycleButton
                label="Pause"
                color="var(--sp-warn)"
                onClick={() => openModal('pause')}
                disabled={!canAct}
                title={canAct ? undefined : disabledReason}
              />
              <LifecycleButton
                label="Resume"
                color="var(--sp-ok)"
                onClick={() => openModal('resume')}
                disabled={!canAct}
                title={canAct ? undefined : disabledReason}
              />
            </div>

            {!canAct ? (
              <div style={{ color: 'var(--sp-text-dim)', fontSize: 10, marginTop: 8 }}>
                {disabledReason}
              </div>
            ) : null}

            {latest ? (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  border: '1px solid var(--sp-border)',
                  borderRadius: 3,
                  fontSize: 11,
                  color: 'var(--sp-text-muted)',
                }}
              >
                <div>Last operation: {latest.operation}</div>
                <div>Halted runs: {latest.halted_runs}</div>
                <div>Cancelled approvals: {latest.cancelled_approvals}</div>
                <div>Affected delegations: {latest.affected_delegations}</div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {modal ? (
        <LifecycleModal
          operation={modal.operation}
          reason={reason}
          onReasonChange={setReason}
          quarantineUntil={quarantineUntil}
          onQuarantineUntilChange={setQuarantineUntil}
          validationError={validationError}
          actionError={actionError}
          submitting={submitting}
          onCancel={closeModal}
          onSubmit={submit}
        />
      ) : null}

      {toast ? (
        <div
          role="status"
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            padding: '6px 10px',
            background: 'var(--sp-ok)',
            color: 'var(--sp-bg, #000)',
            fontSize: 11,
            borderRadius: 3,
            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
            maxWidth: '90%',
          }}
        >
          {toast}
        </div>
      ) : null}
    </div>
  );
}

interface LifecycleButtonProps {
  label: string;
  color: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}

function LifecycleButton({ label, color, onClick, disabled, title }: LifecycleButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        fontSize: 11,
        padding: '4px 12px',
        border: `1px solid ${disabled ? 'var(--sp-border)' : color}`,
        color: disabled ? 'var(--sp-text-dim)' : color,
        background: 'transparent',
        borderRadius: 3,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

interface LifecycleModalProps {
  operation: Operation;
  reason: string;
  onReasonChange: (next: string) => void;
  quarantineUntil: string;
  onQuarantineUntilChange: (next: string) => void;
  validationError: string | null;
  actionError: string | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

function LifecycleModal({
  operation,
  reason,
  onReasonChange,
  quarantineUntil,
  onQuarantineUntilChange,
  validationError,
  actionError,
  submitting,
  onCancel,
  onSubmit,
}: LifecycleModalProps) {
  useEscapeKey(() => {
    if (!submitting) onCancel();
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div
        style={{
          width: 420,
          background: 'var(--sp-bg-panel, #111)',
          border: '1px solid var(--sp-border)',
          borderRadius: 4,
          padding: 16,
          fontSize: 12,
          color: 'var(--sp-text)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--sp-text-dim)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 8,
          }}
        >
          Confirm {operation}
        </div>
        {operation === 'quarantine' ? (
          <label style={{ display: 'block', marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 4 }}>
              Quarantine until (optional — omit for indefinite)
            </div>
            <input
              type="datetime-local"
              value={quarantineUntil}
              onChange={(e) => onQuarantineUntilChange(e.target.value)}
              disabled={submitting}
              style={{
                width: '100%',
                background: 'transparent',
                border: '1px solid var(--sp-border)',
                borderRadius: 3,
                padding: 6,
                color: 'var(--sp-text)',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            />
          </label>
        ) : null}
        <label style={{ display: 'block', marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 4 }}>
            Reason (optional, recorded in audit log)
          </div>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            disabled={submitting}
            rows={3}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid var(--sp-border)',
              borderRadius: 3,
              padding: 6,
              color: 'var(--sp-text)',
              fontSize: 12,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </label>
        {validationError ? (
          <div style={{ color: 'var(--sp-error)', fontSize: 11, marginBottom: 8 }}>
            {validationError}
          </div>
        ) : null}
        {actionError ? (
          <div style={{ color: 'var(--sp-error)', fontSize: 11, marginBottom: 8 }}>
            {actionError}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: '1px solid var(--sp-border)',
              background: 'transparent',
              color: 'var(--sp-text-muted)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: '1px solid var(--sp-accent)',
              background: 'var(--sp-accent)',
              color: 'var(--sp-bg, #000)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '…' : `Confirm ${operation}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export const agentLifecycleControlsWidget: WidgetDefinition = {
  id: 'agent-lifecycle-controls',
  name: 'Agent Lifecycle Controls',
  description: 'Quarantine, pause, or resume an agent identity. Director or owner-only.',
  icon: 'shield',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 6, h: 6 },
  minSize: { w: 4, h: 4 },
  component: AgentLifecycleControlsWidget,
};
